import { useCallback, useEffect, type Dispatch, type SetStateAction } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth, useWebViewAuth } from "@/components/AuthProvider";
import { Button } from "@/components/ui/button";
import { getAccessToken } from "@/services/authService";
import { enableAuthStayOnPage } from "@/utils/authStayOnPage";
import {
  clearPendingNavigation,
  locationToPendingPath,
  setPendingNavigation,
} from "@/utils/authPendingNavigation";

type AuthMode = "login" | "register";

type PrivateRouteProps = {
  setShowAuthModal?: Dispatch<SetStateAction<boolean>>;
  setAuthMode?: Dispatch<SetStateAction<AuthMode>>;
};

/**
 * Guards private pages. Guests stay on the deep-link URL, see login,
 * and continue to the same page after a successful session (no bounce to landing).
 */
const PrivateRoute = ({ setShowAuthModal, setAuthMode }: PrivateRouteProps) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { webViewAuth } = useWebViewAuth();

  const isWebViewAuthenticated =
    webViewAuth.isNativeWebView && !!webViewAuth.accessToken;
  const hasSession =
    isAuthenticated || !!getAccessToken() || isWebViewAuthenticated;

  const requestLogin = useCallback(() => {
    setPendingNavigation(locationToPendingPath(location));
    enableAuthStayOnPage();
    setAuthMode?.("login");
    setShowAuthModal?.(true);
  }, [location, setAuthMode, setShowAuthModal]);

  useEffect(() => {
    if (hasSession) {
      // URL already matches the intended private page; avoid a second resume navigate.
      clearPendingNavigation();
      return;
    }
    requestLogin();
  }, [hasSession, requestLogin]);

  if (hasSession) {
    return <Outlet />;
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 py-16 text-center">
      <h1 className="text-xl font-semibold text-gray-900">Sign in to continue</h1>
      <p className="max-w-md text-sm text-gray-600">
        This page requires an account. Sign in to continue to the page you requested.
      </p>
      <Button
        type="button"
        onClick={requestLogin}
        className="rounded-xl bg-brand px-6 text-white hover:bg-brand-dark"
      >
        Sign in
      </Button>
    </div>
  );
};

export default PrivateRoute;
